import { STATUS_ORDER } from "./constants";

export const VITAL_CODES = ["lcp", "inp", "cls", "fcp", "ttfb"];
export const VITAL_SHORT = {
  lcp: "LCP",
  inp: "INP",
  cls: "CLS",
  fcp: "FCP",
  ttfb: "TTFB",
};

/** Format a Core Web Vital value for display (ms vitals or unitless CLS). */
export const formatVital = (code, value) => {
  if (value == null || Number.isNaN(Number(value))) return "—";
  const n = Number(value);
  if (code === "cls") return n.toFixed(3);
  if (n >= 1000) return `${(n / 1000).toFixed(1)}s`;
  return `${Math.round(n)}ms`;
};

/** Pull vital values from audit/page-speed check evidence. */
export const vitalsFromChecks = (checks) => {
  const byCode = {};
  (checks || []).forEach((check) => {
    if (VITAL_CODES.includes(check.code) && check.evidence?.value != null) {
      byCode[check.code] = {
        code: check.code,
        value: check.evidence.value,
        status: check.status,
        label: check.label || VITAL_SHORT[check.code],
        detail: check.detail,
      };
    }
  });
  return VITAL_CODES.map((code) => byCode[code]).filter(Boolean);
};

export const tallyChecks = (checks) => {
  const counts = { pass: 0, warn: 0, fail: 0, skipped: 0 };
  (checks || []).forEach((check) => {
    if (counts[check.status] != null) counts[check.status] += 1;
  });
  return counts;
};

/** Aggregate pass/warn/fail/skipped counts per check code across report scores. */
export const tallyChecksByCode = (scores) => {
  const byCode = new Map();
  (scores || []).forEach((score) => {
    (score.checks || []).forEach((check) => {
      const code = check.code || "";
      if (!code) return;
      let row = byCode.get(code);
      if (!row) {
        row = {
          code,
          label: check.label || code,
          help: check.help || "",
          importance: check.importance ?? 40,
          pass: 0,
          warn: 0,
          fail: 0,
          skipped: 0,
        };
        byCode.set(code, row);
      } else if (!row.help && check.help) {
        row.help = check.help;
      }
      if (row[check.status] != null) row[check.status] += 1;
    });
  });
  return Array.from(byCode.values()).sort((a, b) => {
    const aRank = a.fail * 2 + a.warn;
    const bRank = b.fail * 2 + b.warn;
    if (bRank !== aRank) return bRank - aRank;
    if ((b.importance || 0) !== (a.importance || 0)) {
      return (b.importance || 0) - (a.importance || 0);
    }
    return a.label.localeCompare(b.label);
  });
};

/** Shape a report check into the Issues-tab issue payload for IssueDrawer. */
export const buildCheckIssue = (report, checkCode) => {
  if (!report || !checkCode) return null;
  let label = checkCode;
  let help = "";
  let importance = 40;
  const pages = [];
  let fail_count = 0;
  let warn_count = 0;

  const suggestionByPage = {};
  (report.suggestions || []).forEach((suggestion) => {
    if (suggestion.check_code === checkCode && suggestion.site_page) {
      suggestionByPage[suggestion.site_page] = suggestion;
    }
  });

  (report.scores || []).forEach((score) => {
    const check = (score.checks || []).find((item) => item.code === checkCode);
    if (!check) return;
    label = check.label || label;
    help = check.help || help;
    if (check.importance != null) importance = check.importance;
    if (!["fail", "warn", "pass", "skipped"].includes(check.status)) return;
    const suggestion = suggestionByPage[score.site_page];
    const probs = check.evidence?.probabilities || {};
    const statusProb = probs[check.status];
    pages.push({
      site_page: score.site_page,
      site_page_path: score.site_page_path,
      preview_path: check.evidence?.sample_path || score.site_page_route || "",
      status: check.status,
      detail: check.detail || "",
      evidence_value: check.evidence?.value ?? null,
      probability_pct: statusProb != null ? Math.round(Number(statusProb) * 100) : null,
      suggestion: suggestion?.hash || null,
      suggestion_status: suggestion?.status || null,
      agent_type: suggestion?.agent_type || (suggestion ? "coding" : null),
    });
    if (check.status === "fail") fail_count += 1;
    else if (check.status === "warn") warn_count += 1;
  });

  pages.sort((a, b) => (STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9));

  return {
    key: `${report.type}:${checkCode}`,
    report_type: report.type,
    report_hash: report.hash,
    check_code: checkCode,
    label,
    help,
    importance,
    fail_count,
    warn_count,
    pages,
  };
};

export const pageMatches = (page, filter) => {
  if (filter === "all") return true;
  const audit = page.audit || page.page_speed || page.ux_audit;
  if (filter === "skipped") return (audit?.checks || []).some((check) => check.status === "skipped");
  return audit?.grade === filter;
};

export const checksForFilter = (checks, filter, includePassing) => {
  if (filter === "skipped") return checks.filter((check) => check.status === "skipped");
  if (filter === "pass" || filter === "warn" || filter === "fail") {
    return checks.filter((check) => check.status === filter);
  }
  if (includePassing) {
    return [...checks].sort((a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status]);
  }
  return checks
    .filter((check) => check.status === "fail" || check.status === "warn")
    .sort((a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status]);
};

export const checkPrimary = (evidence = {}) => (
  evidence.value
  || (Array.isArray(evidence.values) && evidence.values.filter(Boolean).join(" · "))
  || evidence.expression
  || (evidence.lang ? `lang="${evidence.lang}"` : "")
  || ""
);

export const demandEstimate = (query) => {
  const estimate = query?.metrics?.estimate;
  if (estimate?.mid != null) return estimate.mid;
  return query?.volume ?? null;
};

export const formatVolume = (n) => {
  if (n == null || Number.isNaN(Number(n))) return "—";
  const value = Math.round(Number(n));
  const abs = Math.abs(value);
  const compact = (scaled, suffix) => {
    const rounded = abs >= scaled * 10 ? Math.round(value / scaled) : Math.round((value / scaled) * 10) / 10;
    return `${rounded}${suffix}`;
  };
  if (abs >= 1000000) return compact(1000000, "m");
  if (abs >= 1000) return compact(1000, "k");
  return String(value);
};

export const groupSuggestions = (items) => {
  const groups = new Map();
  items.forEach((item) => {
    const key = item.check_code
      ? `${item.report_type || "report"}:${item.check_code}`
      : item.title;
    const title = item.check_label || item.title;
    const existing = groups.get(key);
    if (existing) {
      existing.items.push(item);
      return;
    }
    groups.set(key, {
      key,
      title,
      check_code: item.check_code,
      report_type: item.report_type,
      help: item.help,
      items: [item],
    });
  });
  return Array.from(groups.values()).sort((a, b) => b.items.length - a.items.length);
};

/** Build selectable fix rows from suggestion-bearing page/check entries. */
export const selectableFixItems = (rows) =>
  (rows || [])
    .filter((row) => row.suggestion && row.agent_type === "coding")
    .filter((row) => row.suggestion_status === "open" || row.status === "fail" || row.status === "warn")
    .map((row) => ({
      id: row.suggestion,
      hash: row.suggestion,
      severity: row.status || row.severity || "warn",
      status: row.suggestion_status || "open",
      agent_type: row.agent_type || "coding",
      path: row.site_page_path || row.path || "",
      label: row.label || row.detail || row.site_page_path || "",
      disabled: row.status === "pass" || row.status === "skipped",
    }));

export const defaultSelectedHashes = (items) =>
  items
    .filter((item) => item.severity === "fail" && item.status === "open" && item.agent_type === "coding")
    .map((item) => item.hash);

export const latestHeadlines = (reports) => {
  const byType = {};
  (reports || []).forEach((report) => {
    if (report.is_latest && report.status === "succeeded" && report.headline) {
      byType[report.type] = report;
    }
  });
  return byType;
};

/** Display path like brands/[slug]/ or brands/ from SitePage.path or a raw path string. */
export const formatTemplatePath = (pageOrPath) => {
  let rel = typeof pageOrPath === "string" ? pageOrPath : (pageOrPath?.path || "");
  if (rel.startsWith("src/pages/")) rel = rel.slice("src/pages/".length);
  if (rel.endsWith(".astro")) rel = rel.slice(0, -".astro".length);
  const parts = rel.split("/").filter(Boolean);
  if (!parts.length || (parts.length === 1 && parts[0] === "index")) return "/";
  if (parts[parts.length - 1] === "index") parts.pop();
  return `${parts.join("/")}/`;
};

/** Higher = more problematic. Fails count 2× importance, warns 1×. */
export const pageProblemScore = (checks) => {
  let score = 0;
  (checks || []).forEach((check) => {
    const importance = check.importance
      ?? (check.status === "fail" ? 50 : check.status === "warn" ? 35 : 0);
    if (check.status === "fail") score += importance * 2;
    else if (check.status === "warn") score += importance;
  });
  return score;
};

export const formatReportDate = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString(undefined, {
    month: "numeric",
    day: "numeric",
    year: "numeric",
  });
};

export const formatReportDateTime = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

/** Pick a page-level audit report (page audit, UX, or page speed) for "from …" titles. */
export const resolveAuditReport = (reports, reportFilter, auditHash) => {
  const list = reports || [];
  const isPageScoreType = (type) => (
    type === "page_audit" || type === "ux_audit" || type === "page_speed"
  );
  if (reportFilter) {
    const picked = list.find((r) => r.hash === reportFilter);
    if (picked && isPageScoreType(picked.type)) return picked;
  }
  if (auditHash) {
    const byHash = list.find((r) => r.hash === auditHash);
    if (byHash) return byHash;
  }
  return list.find((r) => r.type === "page_audit" && r.is_latest && r.status === "succeeded")
    || list.find((r) => r.type === "page_audit" && r.status === "succeeded")
    || list.find((r) => r.type === "ux_audit" && r.is_latest && r.status === "succeeded")
    || null;
};

/** Sibling trend points from the reports list (for sidebar mini charts). */
export const listTrendPoints = (reports, type) =>
  (reports || [])
    .filter((r) => r.type === type && r.status === "succeeded")
    .slice()
    .sort((a, b) => new Date(a.finished_at || a.create_date) - new Date(b.finished_at || b.create_date))
    .map((r) => ({
      hash: r.hash,
      finished_at: r.finished_at || r.create_date,
      is_current: !!r.is_latest,
      searches: r.type === "demand" ? (r.headline?.value ?? r.usage?.estimate_mid ?? 0) : undefined,
      fail: r.headline?.fails ?? r.usage?.grades?.fail ?? 0,
      warn: r.headline?.warns ?? r.usage?.grades?.warn ?? 0,
      pass: r.usage?.grades?.pass ?? 0,
      lcp: r.usage?.vitals?.lcp,
      inp: r.usage?.vitals?.inp,
      cls: r.usage?.vitals?.cls,
      fcp: r.usage?.vitals?.fcp,
      ttfb: r.usage?.vitals?.ttfb,
    }));
